import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";

const STORAGE_KEY = "medily.cookieChoice";
export const OPEN_COOKIE_BANNER_EVENT = "medily:open-cookie-banner";

/**
 * Small, premium bottom-right cookie consent banner.
 * - Two actions only: Accept Cookies / Reject Cookies (persists to localStorage).
 * - Appears on first visit; hides once a choice is made.
 * - Can be reopened at any time by dispatching `medily:open-cookie-banner`
 *   (used by the footer "Cookie Preferences" link).
 */
const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing =
      typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!existing) setVisible(true);

    const reopen = () => setVisible(true);
    window.addEventListener(OPEN_COOKIE_BANNER_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_BANNER_EVENT, reopen);
  }, []);

  const save = (choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
      window.localStorage.setItem(`${STORAGE_KEY}.at`, new Date().toISOString());
    } catch (_) {
      /* storage may be unavailable in some browsers — ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed z-[80] left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:right-6 bottom-4 md:bottom-6 w-[min(92vw,420px)] animate-[cookieIn_.35s_ease-out]"
    >
      <div className="relative rounded-2xl bg-white/95 backdrop-blur-md border border-slate-100 shadow-[0_28px_60px_-20px_rgba(29,78,216,0.28)] p-5 md:p-6">
        {/* subtle blue accent glow behind card */}
        <span className="pointer-events-none absolute -inset-4 -z-10 rounded-[28px] bg-[radial-gradient(120px_120px_at_0%_100%,rgba(29,78,216,0.10),transparent_70%)]" />

        <button
          type="button"
          aria-label="Dismiss cookie banner"
          onClick={() => save("rejected")}
          className="absolute top-3 right-3 w-7 h-7 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition inline-flex items-center justify-center"
          data-testid="cookie-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <span className="mt-0.5 w-9 h-9 shrink-0 rounded-full bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center">
            <Cookie className="w-4.5 h-4.5" strokeWidth={1.9} />
          </span>
          <div className="min-w-0">
            <h2
              className="text-[15.5px] font-semibold text-slate-900 tracking-tight"
              data-testid="cookie-heading"
            >
              Your privacy matters
            </h2>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
              Medily uses cookies to keep the website secure, improve your experience and
              understand how the site is used.{" "}
              <Link
                to="/cookie-policy"
                onClick={() => setVisible(false)}
                className="text-[#1d4ed8] font-medium underline underline-offset-2 decoration-[#1d4ed8]/40 hover:decoration-[#1d4ed8]"
                data-testid="cookie-policy-link"
              >
                Read our Cookie Policy
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => save("rejected")}
            className="flex-1 h-10 rounded-full text-[13.5px] font-medium text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition"
            data-testid="cookie-reject-btn"
          >
            Reject Cookies
          </button>
          <button
            type="button"
            onClick={() => save("accepted")}
            className="flex-1 h-10 rounded-full text-[13.5px] font-semibold text-white bg-[#1d4ed8] hover:bg-[#1e40af] shadow-[0_10px_24px_-10px_rgba(29,78,216,0.6)] transition"
            data-testid="cookie-accept-btn"
          >
            Accept Cookies
          </button>
        </div>
      </div>

      <style>{`
        @keyframes cookieIn {
          from { opacity: 0; transform: translate(var(--tw-translate-x,0), 20px); }
          to   { opacity: 1; transform: translate(var(--tw-translate-x,0), 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid='cookie-banner'] { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default CookieBanner;
